import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import {
  PRIMARY,
  TEXT_DARK,
  TEXT_MUTED,
  BG_CARD,
  BORDER_LIGHT,
  FONT_HEADING,
} from '../components/legacy-ball/table-management/designTokens';

// The 8 booklet sections (same as WeekendBookletPage exports)
const BOOKLET_SECTIONS = [
  'Front Matter',
  'Feature Stories',
  'Heritage & History',
  'Faith & Culture',
  'People & Community',
  'Culture & Lifestyle',
  'KDU Global Impact',
  'Looking Forward',
] as const;

interface BookletArticle {
  id: string;
  title: string;
  body: string | null;
  authorName: string;
  bookletSection: string | null;
  status: string;
  createdAt: string;
}

interface EditionInfo {
  id: string;
  name: string;
}

// Truncate body for summary
function getSummary(body: string | null, maxLen = 200): string {
  if (!body) return 'No content available.';
  const text = body.replace(/<[^>]+>/g, '').trim();
  if (text.length <= maxLen) return text;
  return text.slice(0, maxLen) + '\u2026';
}

/**
 * BookletBrowserPage — Public-facing (attendee) view of the Weekend Booklet articles.
 * Shows section pills with article counts, vertical card listing, and article detail on tap.
 */
export function BookletBrowserPage() {
  const navigate = useNavigate();
  const [articles, setArticles] = useState<BookletArticle[]>([]);
  const [edition, setEdition] = useState<EditionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<string>('All');
  const [selectedArticle, setSelectedArticle] = useState<BookletArticle | null>(null);
  const [approvedOnly, setApprovedOnly] = useState(false);
  const [articleSummary, setArticleSummary] = useState<string | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // Touch swipe refs — MUST be before any conditional returns
  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);

  // Fetch articles on mount
  useEffect(() => {
    let cancelled = false;
    async function fetchArticles() {
      try {
        const ed = await apiClient<EditionInfo>('/weekend-booklet/editions/active');
        if (cancelled) return;
        if (!ed || !ed.id) {
          setArticles([]);
          setLoading(false);
          return;
        }
        setEdition(ed);

        const subs = await apiClient<BookletArticle[]>(
          `/weekend-booklet/submissions?editionId=${ed.id}&sectionCategory=GeneralArticles`
        );
        if (cancelled) return;
        setArticles(subs);
      } catch (err: any) {
        if (cancelled) return;
        if (err?.status === 403 || err?.message?.includes('403')) {
          setError('You do not have access to browse booklet articles. Please contact an organizer.');
        } else {
          setError('Unable to load booklet articles.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchArticles();
    return () => { cancelled = true; };
  }, []);

  // Fetch summary when selectedArticle changes
  useEffect(() => {
    if (!selectedArticle) {
      setArticleSummary(null);
      setSummaryLoading(false);
      return;
    }

    let cancelled = false;
    setSummaryLoading(true);
    setArticleSummary(null);

    apiClient<{ summary: string }>(
      `/weekend-booklet/submissions/${selectedArticle.id}/summary`
    )
      .then((data) => {
        if (!cancelled) setArticleSummary(data.summary);
      })
      .catch(() => {
        if (!cancelled) setArticleSummary(getSummary(selectedArticle.body, 300));
      })
      .finally(() => {
        if (!cancelled) setSummaryLoading(false);
      });

    return () => { cancelled = true; };
  }, [selectedArticle]);

  // Base articles (optionally filtered by approved status)
  const baseArticles = approvedOnly
    ? articles.filter((a) => a.status === 'Approved' || a.status === 'ReadyForLayout')
    : articles;

  // Group articles by bookletSection
  const sectionCounts: Record<string, number> = {};
  let totalCount = 0;
  BOOKLET_SECTIONS.forEach((s) => { sectionCounts[s] = 0; });
  sectionCounts['Other'] = 0;

  baseArticles.forEach((a) => {
    totalCount++;
    const section = a.bookletSection || 'Other';
    if (sectionCounts[section] !== undefined) {
      sectionCounts[section]++;
    } else {
      sectionCounts['Other']++;
    }
  });

  // Filter articles by active section
  const filteredArticles =
    activeSection === 'All'
      ? baseArticles
      : baseArticles.filter((a) => (a.bookletSection || 'Other') === activeSection);

  // Sections that have articles (for pills)
  const activeSections = BOOKLET_SECTIONS.filter((s) => sectionCounts[s] > 0);

  // Swipe navigation between articles
  const currentArticleIndex = selectedArticle
    ? filteredArticles.findIndex((a) => a.id === selectedArticle.id)
    : -1;

  const goNext = useCallback(() => {
    const idx = selectedArticle
      ? filteredArticles.findIndex((a) => a.id === selectedArticle.id)
      : -1;
    if (idx < filteredArticles.length - 1) {
      setSelectedArticle(filteredArticles[idx + 1]);
    }
  }, [selectedArticle, filteredArticles]);

  const goPrev = useCallback(() => {
    const idx = selectedArticle
      ? filteredArticles.findIndex((a) => a.id === selectedArticle.id)
      : -1;
    if (idx > 0) {
      setSelectedArticle(filteredArticles[idx - 1]);
    }
  }, [selectedArticle, filteredArticles]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    touchEndX.current = e.changedTouches[0].clientX;
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 60) {
      if (diff > 0) goNext();
      else goPrev();
    }
  }, [goNext, goPrev]);

  // ── Render ──────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={styles.page}>
        <p style={{ color: TEXT_MUTED, textAlign: 'center', padding: '2rem' }}>Loading booklet…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.page}>
        <div style={styles.errorBanner}>{error}</div>
        <button style={styles.backBtn} onClick={() => navigate(-1)}>← Back</button>
      </div>
    );
  }

  // Article detail view
  if (selectedArticle) {
    return (
      <div style={styles.page} className="notranslate" translate="no" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
        <button style={styles.backBtn} onClick={() => setSelectedArticle(null)}>
          ← Back to Articles
        </button>
        <div style={styles.detailCard}>
          {/* Article position indicator */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', fontSize: '0.75rem', color: TEXT_MUTED }}>
            <span>{currentArticleIndex + 1} of {filteredArticles.length}</span>
            <span style={{ fontSize: '0.7rem' }}>← swipe →</span>
          </div>
          <p style={{ fontSize: '0.75rem', fontWeight: 700, color: '#b45309', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 0.4rem' }}>Article Preview</p>
          <h2 style={styles.detailTitle}>{selectedArticle.title}</h2>
          <p style={styles.detailAuthor}>By {selectedArticle.authorName}</p>
          {selectedArticle.bookletSection && (
            <span style={styles.sectionTag}>{selectedArticle.bookletSection}</span>
          )}
          <div style={styles.detailBody}>
            {summaryLoading ? (
              <p style={{ color: TEXT_MUTED, fontStyle: 'italic' }}>✨ Generating summary…</p>
            ) : articleSummary ? (
              <p>{articleSummary}</p>
            ) : (
              <p style={{ color: TEXT_MUTED }}>No summary available.</p>
            )}
          </div>
          {/* Read full article caption */}
          <div style={{ marginTop: '1.25rem', padding: '0.75rem 1rem', backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', textAlign: 'center' as const }}>
            <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: 600, color: '#92400e' }}>
              📖 Read the full article in the Weekend Booklet
            </p>
          </div>
          {/* Prev/Next buttons */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1rem', gap: '0.5rem' }}>
            <button
              onClick={goPrev}
              disabled={currentArticleIndex <= 0}
              style={{ flex: 1, padding: '10px', border: `1px solid ${BORDER_LIGHT}`, borderRadius: '6px', backgroundColor: currentArticleIndex > 0 ? BG_CARD : '#f3f4f6', color: currentArticleIndex > 0 ? TEXT_DARK : TEXT_MUTED, cursor: currentArticleIndex > 0 ? 'pointer' : 'default', fontSize: '0.85rem' }}
              aria-label="Previous article"
            >
              ← Previous
            </button>
            <button
              onClick={goNext}
              disabled={currentArticleIndex >= filteredArticles.length - 1}
              style={{ flex: 1, padding: '10px', border: `1px solid ${BORDER_LIGHT}`, borderRadius: '6px', backgroundColor: currentArticleIndex < filteredArticles.length - 1 ? BG_CARD : '#f3f4f6', color: currentArticleIndex < filteredArticles.length - 1 ? TEXT_DARK : TEXT_MUTED, cursor: currentArticleIndex < filteredArticles.length - 1 ? 'pointer' : 'default', fontSize: '0.85rem' }}
              aria-label="Next article"
            >
              Next →
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main listing view
  return (
    <div style={styles.page} className="notranslate" translate="no">
      {/* Header */}
      <button style={styles.backBtn} onClick={() => navigate(-1)}>
        ← Back to Marketplace
      </button>
      <h1 style={styles.title}>📖 Weekend Booklet</h1>
      <p style={styles.subtitle}>
        {edition?.name || 'Current Edition'} — {totalCount} article{totalCount !== 1 ? 's' : ''}
      </p>

      {/* Approved Only toggle */}
      <div style={{ marginBottom: '0.75rem' }}>
        <button
          style={approvedOnly ? styles.toggleActive : styles.toggle}
          onClick={() => setApprovedOnly(!approvedOnly)}
        >
          {approvedOnly ? '✓ Approved Only' : 'Show All Statuses'}
        </button>
      </div>

      {/* Section Pills */}
      <div style={styles.pillsContainer}>
        <button
          style={activeSection === 'All' ? styles.pillActive : styles.pill}
          onClick={() => setActiveSection('All')}
        >
          All ({totalCount})
        </button>
        {activeSections.map((section) => (
          <button
            key={section}
            style={activeSection === section ? styles.pillActive : styles.pill}
            onClick={() => setActiveSection(section)}
          >
            {section} ({sectionCounts[section]})
          </button>
        ))}
      </div>

      {/* Article Cards */}
      {filteredArticles.length === 0 ? (
        <div style={styles.empty}>No articles in this section yet.</div>
      ) : (
        <div style={styles.articleList}>
          {filteredArticles.map((article) => (
            <button
              key={article.id}
              style={styles.articleCard}
              onClick={() => setSelectedArticle(article)}
              aria-label={`Read ${article.title}`}
            >
              <h3 style={styles.articleTitle}>{article.title}</h3>
              <p style={styles.articleAuthor}>By {article.authorName}</p>
              <p style={styles.articleSummary}>{getSummary(article.body, 120)}</p>
              {article.bookletSection && (
                <span style={styles.articleSectionBadge}>{article.bookletSection}</span>
              )}
              {!approvedOnly && (
                <span style={{
                  ...styles.articleSectionBadge,
                  backgroundColor: article.status === 'Approved' || article.status === 'ReadyForLayout' ? '#dcfce7' : '#fef3c7',
                  color: article.status === 'Approved' || article.status === 'ReadyForLayout' ? '#16a34a' : '#d97706',
                }}>
                  {article.status}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    padding: '1.5rem',
    minHeight: '100%',
    maxWidth: '600px',
    margin: '0 auto',
  },
  title: {
    fontFamily: FONT_HEADING,
    fontSize: '1.5rem',
    fontWeight: 700,
    color: TEXT_DARK,
    margin: '0.5rem 0 0.25rem',
  },
  subtitle: {
    color: TEXT_MUTED,
    fontSize: '0.85rem',
    marginBottom: '1rem',
  },
  backBtn: {
    background: 'none',
    border: 'none',
    color: PRIMARY,
    fontWeight: 600,
    fontSize: '0.9rem',
    cursor: 'pointer',
    padding: '4px 0',
    marginBottom: '0.5rem',
    display: 'block',
  },
  errorBanner: {
    backgroundColor: '#fef2f2',
    color: '#991b1b',
    border: '1px solid #fecaca',
    padding: '0.75rem 1rem',
    borderRadius: '6px',
    marginBottom: '1rem',
  },
  pillsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginBottom: '1.25rem',
  },
  pill: {
    backgroundColor: '#f3f4f6',
    color: TEXT_MUTED,
    border: 'none',
    borderRadius: '20px',
    padding: '6px 14px',
    fontSize: '0.75rem',
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
  },
  pillActive: {
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '20px',
    padding: '6px 14px',
    fontSize: '0.75rem',
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
  },
  empty: {
    textAlign: 'center' as const,
    padding: '2rem 1rem',
    color: TEXT_MUTED,
    fontSize: '0.9rem',
  },
  toggle: {
    backgroundColor: '#f3f4f6',
    color: TEXT_MUTED,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '6px',
    padding: '6px 14px',
    fontSize: '0.8rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  toggleActive: {
    backgroundColor: '#dcfce7',
    color: '#16a34a',
    border: '1px solid #bbf7d0',
    borderRadius: '6px',
    padding: '6px 14px',
    fontSize: '0.8rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  articleList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px',
  },
  articleCard: {
    backgroundColor: BG_CARD,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '10px',
    padding: '1rem',
    textAlign: 'left' as const,
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
    width: '100%',
    transition: 'box-shadow 0.15s, transform 0.15s',
  },
  articleTitle: {
    fontFamily: FONT_HEADING,
    fontSize: '1rem',
    fontWeight: 600,
    color: TEXT_DARK,
    margin: 0,
  },
  articleAuthor: {
    fontSize: '0.8rem',
    color: PRIMARY,
    fontWeight: 500,
    margin: 0,
  },
  articleSummary: {
    fontSize: '0.82rem',
    color: TEXT_MUTED,
    lineHeight: 1.4,
    margin: '4px 0 0',
  },
  articleSectionBadge: {
    alignSelf: 'flex-start',
    marginTop: '6px',
    backgroundColor: '#f0fdf4',
    color: PRIMARY,
    fontSize: '0.65rem',
    fontWeight: 600,
    padding: '3px 8px',
    borderRadius: '12px',
  },
  // Detail view
  detailCard: {
    backgroundColor: BG_CARD,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '10px',
    padding: '1.5rem',
    marginTop: '0.5rem',
  },
  detailTitle: {
    fontFamily: FONT_HEADING,
    fontSize: '1.3rem',
    fontWeight: 700,
    color: TEXT_DARK,
    margin: '0 0 0.25rem',
  },
  detailAuthor: {
    fontSize: '0.9rem',
    color: PRIMARY,
    fontWeight: 500,
    marginBottom: '0.5rem',
  },
  sectionTag: {
    display: 'inline-block',
    backgroundColor: '#f0fdf4',
    color: PRIMARY,
    fontSize: '0.7rem',
    fontWeight: 600,
    padding: '3px 10px',
    borderRadius: '12px',
    marginBottom: '1rem',
  },
  detailBody: {
    fontSize: '0.9rem',
    lineHeight: 1.6,
    color: TEXT_DARK,
  },
};
